/* iter55z-j — SuggestionsDrawer: side panel listing pending / resolved
 * suggestions on the current chapter. The project OWNER sees Accept /
 * Decline buttons; everyone else sees the status.
 *
 * Polls every 15s so multi-author sessions stay in sync without needing
 * websockets (per user pick 6a).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MessageSquare, PenSquare, Check, X, Loader2, RefreshCw, Trash2, Wifi, WifiOff, Sparkles, CheckCheck, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
// iter55g34 — SSE keeps the drawer in sync in real time; the polling
// fallback interval is now 60 s (was 15 s) purely as a safety net for
// browsers that don't support EventSource or when the stream drops.
const POLL_MS = 60000;

export default function SuggestionsDrawer({
  open, onClose,
  projectId, chapterId,
  currentUserId, isOwner,
  onChapterChanged,        // called after an accept applied a text change
}) {
  const [items, setItems]   = useState([]);
  const [busy, setBusy]     = useState(false);
  const [filter, setFilter] = useState('pending');   // pending | all
  const [pending, setPending] = useState(null);      // {id, action}
  const [live, setLive]     = useState(false);       // iter55g34 SSE indicator
  const [staleFlash, setStaleFlash] = useState(false);
  const knownIdsRef = useRef(new Set());
  const [aiPendingCount, setAiPendingCount] = useState(0);
  const [acceptAllOpen, setAcceptAllOpen] = useState(false);
  const [acceptAllBusy, setAcceptAllBusy] = useState(false);
  const navigate = useNavigate();

  // Derive a review workspace link from any suggestion that has a review_id
  // (i.e. was added by a human editor via /editor/review/:rid)
  const editorReviewId = items.find((s) => s.review_id)?.review_id || null;

  const loadAiPendingCount = useCallback(async () => {
    if (!projectId) return;
    try {
      const r = await axios.get(`${API}/projects/${projectId}/suggestions`, {
        params: { status: 'pending', kind: 'edit', source: 'ai_proposed' },
        withCredentials: true,
      });
      setAiPendingCount((r.data || []).length);
    } catch { setAiPendingCount(0); }
  }, [projectId]);

  const load = useCallback(async () => {
    if (!projectId || !chapterId) return;
    setBusy(true);
    try {
      const params = { chapter_id: chapterId };
      if (filter === 'pending') params.status = 'pending';
      const r = await axios.get(`${API}/projects/${projectId}/suggestions`,
        { params, withCredentials: true });
      const rows = r.data || [];
      // Detect newly-arrived rows since the last load and toast them.
      const known = knownIdsRef.current;
      if (known.size > 0) {
        const arrived = rows.filter((s) => !known.has(s.id));
        if (arrived.length && arrived.length <= 3) {
          arrived.forEach((s) => {
            const from = s.author_display_name || s.author_email || 'A collaborator';
            const kind = s.kind === 'edit' ? 'suggested an edit' : 'left a comment';
            toast.info(`${from} ${kind}`, { duration: 3500 });
          });
        } else if (arrived.length > 3) {
          toast.info(`${arrived.length} new suggestions`, { duration: 3500 });
        }
      }
      knownIdsRef.current = new Set(rows.map((s) => s.id));
      setItems(rows);
    } catch {
      setItems([]);
    } finally { setBusy(false); }
  }, [projectId, chapterId, filter]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    loadAiPendingCount();
    // iter55z-q — First-time contextual tour for the suggestions drawer.
    const tt = setTimeout(() => {
      import('@/lib/tours').then(({ runTour }) => runTour('suggestions-first-open'));
    }, 700);
    const iv = setInterval(load, POLL_MS);
    return () => { clearTimeout(tt); clearInterval(iv); };
  }, [open, load, loadAiPendingCount]);

  // iter55g34 — Realtime SSE subscription. Backend endpoint already
  // publishes on create/update/delete via `suggestions_pubsub`. On any
  // event we bounce through `load()` so the filter state is honoured
  // and the newly-arrived toast logic runs.
  useEffect(() => {
    if (!open || !projectId || !chapterId) return undefined;
    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      return undefined;
    }
    const url = `${API}/projects/${projectId}/chapters/${chapterId}/suggestions/stream`;
    let es;
    try {
      es = new window.EventSource(url, { withCredentials: true });
    } catch (_e) {
      return undefined;
    }
    const onOpen  = () => setLive(true);
    const onError = () => setLive(false);
    const onEvt   = () => { load(); };
    // Feb-2026 iter — pubsub emits `stale` when a saturated queue was
    // drained. Amber-flash the indicator and refetch once so the
    // author knows the channel just resynced.
    const onStale = () => {
      setStaleFlash(true);
      load();
      setTimeout(() => setStaleFlash(false), 2000);
    };
    es.addEventListener('open', onOpen);
    es.addEventListener('error', onError);
    es.addEventListener('suggestion', onEvt);
    es.addEventListener('stale', onStale);
    return () => {
      es.removeEventListener('open', onOpen);
      es.removeEventListener('error', onError);
      es.removeEventListener('suggestion', onEvt);
      es.removeEventListener('stale', onStale);
      es.close();
      setLive(false);
      setStaleFlash(false);
    };
  }, [open, projectId, chapterId, load]);

  const resolve = async (sid, action) => {
    setPending({ id: sid, action });
    try {
      const r = await axios.post(`${API}/suggestions/${sid}/${action}`, {},
        { withCredentials: true });
      if (action === 'accept' && r.data?.applied) {
        toast.success('Suggestion accepted & applied to chapter.');
        onChapterChanged?.();
      } else if (action === 'accept' && r.data?.applied === false) {
        toast.warning('Accepted, but the text has moved since — apply manually.');
      } else {
        toast.success(`Suggestion ${action}d.`);
      }
      await load();
      await loadAiPendingCount();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update suggestion.');
    } finally { setPending(null); }
  };

  const acceptAllAi = async () => {
    setAcceptAllBusy(true);
    try {
      const r = await axios.post(`${API}/projects/${projectId}/suggestions/accept-all-ai`, {},
        { withCredentials: true });
      const { accepted = 0, applied = 0 } = r.data || {};
      const manual = accepted - applied;
      toast.success(
        `Accepted ${accepted} AI suggestion${accepted === 1 ? '' : 's'}.`
        + (manual > 0 ? ` ${manual} moved since and need manual reconciliation.` : ''),
      );
      setAcceptAllOpen(false);
      await load();
      await loadAiPendingCount();
      onChapterChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept all AI suggestions.');
    } finally { setAcceptAllBusy(false); }
  };

  const doDelete = async (sid) => {
    try {
      await axios.delete(`${API}/suggestions/${sid}`, { withCredentials: true });
      setItems((prev) => prev.filter((s) => s.id !== sid));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete.');
    }
  };

  if (!open) return null;

  return (
    <aside
      data-testid="editor-suggestions-drawer"
      className="fixed right-0 top-0 z-40 flex h-full w-[380px] flex-col border-l border-panel-border bg-panel shadow-xl"
    >
      <header className="flex shrink-0 items-center justify-between border-b border-panel-border bg-panel-header/60 px-3 py-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span className="text-[14px] font-semibold">Suggestions & notes</span>
          <Badge variant="outline" className="h-4 px-1 text-[9.5px]" data-testid="editor-suggestions-count">
            {items.length}
          </Badge>
          {/* iter55g34 — Realtime SSE indicator.
              Feb-2026 iter — amber flash on stale-recover so authors
              see when the channel resynced after a saturated queue. */}
          <span
            data-testid="editor-suggestions-live"
            title={
              staleFlash ? 'Resyncing… — the channel dropped a burst; refreshing now.'
              : live ? 'Live — new suggestions arrive instantly'
              : 'Reconnecting…'
            }
            className={`inline-flex items-center gap-1 rounded-sm px-1 py-0.5 text-[9.5px] font-semibold transition-colors ${
              staleFlash ? 'bg-warning/20 text-warning dark:text-warning/90'
              : live      ? 'bg-success/15 text-success dark:text-success/90'
                            : 'bg-muted text-muted-foreground'
            }`}
          >
            {live ? <Wifi className="h-2.5 w-2.5" /> : <WifiOff className="h-2.5 w-2.5" />}
            {staleFlash ? 'Resync' : live ? 'Live' : 'Offline'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={load} className="h-7 px-2" data-testid="editor-suggestions-refresh">
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose} className="h-7 w-7 p-0" aria-label="Close">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </header>

      {/* Editor review workspace link — shown when human editor suggestions exist */}
      {editorReviewId && isOwner && (
        <div
          className="shrink-0 flex items-center gap-2 border-b border-primary/20 bg-primary/8 px-3 py-2 text-[12px]"
          data-testid="suggestions-review-workspace-banner"
        >
          <ExternalLink className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="flex-1 text-primary/90">Your editor has left markups on this manuscript.</span>
          <button
            type="button"
            onClick={() => navigate(`/editor/review/${editorReviewId}`)}
            className="shrink-0 rounded-sm bg-primary px-2 py-0.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
            data-testid="suggestions-open-review-workspace-btn"
          >
            View full review
          </button>
        </div>
      )}

      <div className="shrink-0 border-b border-panel-border bg-panel-header/40 px-3 py-1.5 text-[13px]">
        <div className="flex items-center gap-1">
          {['pending', 'all'].map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              data-testid={`editor-suggestions-filter-${k}`}
              className={`rounded-sm px-2 py-0.5 capitalize ${
                filter === k ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {k}
            </button>
          ))}
          <span className="ml-auto text-[13px] text-muted-foreground">
            Polls every 15s
          </span>
        </div>
        {isOwner && aiPendingCount > 0 && (
          <div className="mt-1.5">
            <AlertDialog open={acceptAllOpen} onOpenChange={setAcceptAllOpen}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAcceptAllOpen(true)}
                className="h-7 w-full gap-1.5 border-primary/40 text-[13px] text-primary hover:bg-primary/10"
                data-testid="editor-suggestions-accept-all-ai-btn"
              >
                <Sparkles className="h-3 w-3" />
                Accept all AI suggestions
                <Badge variant="outline" className="ml-auto h-4 px-1 text-[9.5px]">{aiPendingCount}</Badge>
              </Button>
              <AlertDialogContent data-testid="editor-suggestions-accept-all-ai-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <CheckCheck className="h-4 w-4 text-primary" /> Accept all AI suggestions?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This accepts and applies all <b>{aiPendingCount}</b> pending
                    AI-proposed edit{aiPendingCount === 1 ? '' : 's'} across this manuscript —
                    each one rewrites the matching chapter text. This will edit chapter
                    text and cannot be bulk-undone. Suggestions from human co-editors
                    are not affected and still need individual review.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="editor-suggestions-accept-all-ai-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => { e.preventDefault(); acceptAllAi(); }}
                    disabled={acceptAllBusy}
                    data-testid="editor-suggestions-accept-all-ai-confirm"
                  >
                    {acceptAllBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Accept all ${aiPendingCount}`}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      <div className="scroll-thin flex-1 space-y-2 overflow-y-auto p-3">
        {items.length === 0 && !busy && (
          <p className="rounded-md border border-dashed border-panel-border/60 p-6 text-center text-[13.5px] text-muted-foreground" data-testid="editor-suggestions-empty">
            No {filter === 'pending' ? 'pending ' : ''}suggestions on this chapter yet.
            Highlight any passage in the manuscript and click <b>Suggest edit</b> or <b>Add note</b>.
          </p>
        )}
        {items.map((s) => {
          const isAuthor = s.author_user_id === currentUserId;
          const canResolve = isOwner && s.status === 'pending';
          const canDelete  = isAuthor || isOwner;
          return (
            <div
              key={s.id}
              className={`rounded-md border p-2.5 ${
                s.status === 'pending'  ? 'border-panel-border bg-card' :
                s.status === 'accepted' ? 'border-success/60 bg-success/20' :
                                          'border-border bg-muted'
              }`}
              data-testid={`editor-suggestion-item-${s.id}`}
            >
              <div className="mb-1 flex items-center gap-2 text-[13px] uppercase tracking-wider text-muted-foreground">
                {s.kind === 'edit'
                  ? <PenSquare className="h-3 w-3 text-primary" />
                  : <MessageSquare className="h-3 w-3 text-success" />}
                <span>{s.kind === 'edit' ? 'Edit' : 'Note'} · {s.author_name}</span>
                {s.status !== 'pending' && (
                  <Badge variant="outline" className="ml-auto h-4 px-1 text-[9px] capitalize">
                    {s.status}
                  </Badge>
                )}
              </div>
              {/* Feb-2026 · Bumped background opacity on all inner blocks
               * so text on the drawer no longer looks washed out — the
               * anchor quote, proposed text, note field, and inline diff
               * are now solid enough to read at any zoom level. */}
              <div className="rounded-sm border border-border bg-secondary p-2 text-[13.5px] italic text-foreground">
                &ldquo;{(s.anchor_text || '').slice(0, 200)}{(s.anchor_text || '').length > 200 ? '…' : ''}&rdquo;
              </div>
              {s.kind === 'edit' && s.proposed_text && (
                <div className="mt-1.5 rounded-sm border border-primary/30 bg-primary/15 p-2 text-[13.5px] text-foreground" data-testid={`editor-suggestion-diff-${s.id}`}>
                  <span className="text-[13px] font-medium uppercase tracking-wider text-primary">Proposed:</span>{' '}
                  {s.proposed_text}
                  {s.status === 'pending' && (
                    <details className="mt-1.5 rounded-sm border border-border bg-background p-1.5">
                      <summary className="cursor-pointer select-none text-[13px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                        Show diff
                      </summary>
                      <div className="mt-1 space-y-1">
                        <p className="rounded-sm border border-destructive/40 bg-destructive/20 px-1.5 py-1 text-[13.5px] leading-snug text-destructive line-through decoration-destructive/70" data-testid={`editor-suggestion-diff-old-${s.id}`}>
                          {(s.anchor_text || '').slice(0, 500)}
                          {(s.anchor_text || '').length > 500 ? '…' : ''}
                        </p>
                        <p className="rounded-sm border border-success/40 bg-success/20 px-1.5 py-1 text-[13.5px] leading-snug text-success" data-testid={`editor-suggestion-diff-new-${s.id}`}>
                          {(s.proposed_text || '').slice(0, 500)}
                          {(s.proposed_text || '').length > 500 ? '…' : ''}
                        </p>
                      </div>
                    </details>
                  )}
                </div>
              )}
              {s.note && (
                <div className="mt-1.5 rounded-sm border border-border bg-secondary p-2 text-[13.5px] text-foreground" data-testid={`editor-suggestion-note-${s.id}`}>
                  <span className="text-[13px] font-medium uppercase tracking-wider text-muted-foreground">Note:</span>{' '}
                  {s.note}
                </div>
              )}
              <div className="mt-2 flex items-center gap-1.5">
                <span className="text-[13px] text-muted-foreground">
                  {new Date(s.created_at).toLocaleString()}
                </span>
                <div className="ml-auto flex items-center gap-1">
                  {canResolve && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!!pending}
                        onClick={() => resolve(s.id, 'decline')}
                        className="h-7 gap-1 px-2 text-[13px]"
                        data-testid={`editor-suggestion-decline-${s.id}`}
                      >
                        {pending?.id === s.id && pending.action === 'decline'
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <X className="h-3 w-3" />} Decline
                      </Button>
                      <Button
                        size="sm"
                        disabled={!!pending}
                        onClick={() => resolve(s.id, 'accept')}
                        className="h-7 gap-1 px-2 text-[13px]"
                        data-testid={`editor-suggestion-accept-${s.id}`}
                      >
                        {pending?.id === s.id && pending.action === 'accept'
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : <Check className="h-3 w-3" />} Accept
                      </Button>
                    </>
                  )}
                  {canDelete && s.status !== 'pending' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => doDelete(s.id)}
                      title="Delete suggestion"
                      data-testid={`editor-suggestion-delete-${s.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
